// Extracted Round 3 helpers (Trends / Creator Analysis / Performance / Insights).
import React from 'react';
import { Copy, Gauge, Trash2 } from 'lucide-react';

// ===== Round 3 helpers =====

function TrendReportView({ trend, onCopy, onUseHook }) {
  const r = trend?.report || {};
  return (
    <div className="space-y-3" data-testid="trend-report">
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-bold text-[#0A0F1C]">{trend.niche}</h2>
          <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{new Date(trend.created_at).toLocaleString()}</span>
        </div>
        {r.summary && <p className="text-sm text-[#475569] mt-2 leading-relaxed">{r.summary}</p>}
      </div>

      {r.themes?.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid="trend-themes">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-2">Emerging themes</div>
          <ul className="space-y-2">
            {r.themes.map((t, i) => (
              <li key={t.name || `theme-${i}`} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <strong className="text-sm text-[#0A0F1C]">{t.name}</strong>
                  {t.best_platform && <span className="text-[10px] uppercase tracking-wider text-[#1D4ED8]">{t.best_platform}</span>}
                </div>
                {t.why_now && <p className="text-xs text-[#475569] mt-1">{t.why_now}</p>}
                {t.example_hook && (
                  <div className="flex items-center gap-2 mt-1">
                    <em className="text-xs text-[#0A0F1C]">"{t.example_hook}"</em>
                    <button type="button" onClick={() => onUseHook(t.example_hook)} className="text-[10px] text-[#1D4ED8] underline">Use</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {r.hook_patterns?.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid="trend-hook-patterns">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-2">Hook patterns</div>
          <ul className="space-y-2">
            {r.hook_patterns.map((h, i) => (
              <li key={h.pattern || `hook-${i}`} className="text-xs">
                <strong className="text-[#0A0F1C]">{h.pattern}</strong>
                {h.why_it_works && <span className="text-[#475569]"> - {h.why_it_works}</span>}
                {h.example && (
                  <div className="ml-3 mt-0.5 flex items-center gap-2">
                    <em className="text-[#475569]">e.g. "{h.example}"</em>
                    <button type="button" onClick={() => onCopy(h.example)} className="text-[#64748B] hover:text-[#1D4ED8]">
                      <Copy size={10} aria-hidden="true" />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(r.audio_cues?.length > 0 || r.format_trends?.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-3">
          {r.audio_cues?.length > 0 && (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4" data-testid="trend-audio">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Audio cues</div>
              <ul className="space-y-1">
                {r.audio_cues.map((a, i) => (
                  <li key={a.vibe || `audio-${i}`} className="text-xs"><strong className="text-[#0A0F1C]">{a.vibe}</strong> <span className="text-[#475569]">- {a.use_for}</span></li>
                ))}
              </ul>
            </div>
          )}
          {r.format_trends?.length > 0 && (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4" data-testid="trend-formats">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Format trends</div>
              <ul className="space-y-1">
                {r.format_trends.map((f, i) => (
                  <li key={f.name || `format-${i}`} className="text-xs"><strong className="text-[#0A0F1C]">{f.name}</strong> <span className="text-[#475569]">- {f.description}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {r.opportunity_gaps?.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4" data-testid="trend-gaps">
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-1">Opportunity gaps</div>
          <ul className="space-y-1">
            {r.opportunity_gaps.map((g, i) => (
              <li key={`gap-${i}-${typeof g === 'string' ? g.slice(0, 20) : i}`} className="text-xs text-[#0A0F1C]">→ {g}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CreatorAnalysisView({ analysis, onCopy, onUseRewrite }) {
  const r = analysis?.report || {};
  const fw = r.framework || {};
  return (
    <div className="space-y-3" data-testid="creator-analysis-view">
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-bold text-[#0A0F1C]">{analysis.competitor_handle || 'Competitor'}</h2>
          <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{new Date(analysis.created_at).toLocaleString()}</span>
        </div>
        {r.summary && <p className="text-sm text-[#475569] mt-2 leading-relaxed">{r.summary}</p>}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid="creator-framework">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-2">Their framework</div>
        <div className="grid sm:grid-cols-2 gap-3 text-xs">
          {fw.hook_style && <div><strong>Hook style:</strong> {fw.hook_style}</div>}
          {fw.retention_pattern && <div><strong>Retention pattern:</strong> {fw.retention_pattern}</div>}
          {fw.payoff_type && <div><strong>Payoff:</strong> {fw.payoff_type}</div>}
          {fw.cta_type && <div><strong>CTA:</strong> {fw.cta_type}</div>}
        </div>
        {fw.emotional_buttons?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {fw.emotional_buttons.map((b, i) => <span key={`emo-${i}-${typeof b === 'string' ? b.slice(0, 16) : i}`} className="text-[10px] px-2 py-0.5 bg-[#EFF6FF] text-[#1D4ED8] rounded-full">{b}</span>)}
          </div>
        )}
        {fw.why_it_works && <p className="text-xs text-[#475569] mt-2"><strong>Why it works:</strong> {fw.why_it_works}</p>}
      </div>

      {r.viral_levers?.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid="creator-levers">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-2">Viral levers</div>
          <ul className="space-y-1">
            {r.viral_levers.map((l, i) => (
              <li key={l.name || `lever-${i}`} className="text-xs"><strong className="text-[#0A0F1C]">{l.name}</strong> <span className="text-[#475569]">- {l.detail}</span></li>
            ))}
          </ul>
        </div>
      )}

      {r.weaknesses?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="creator-weaknesses">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1">Weaknesses / compliance risks</div>
          <ul className="space-y-1">
            {r.weaknesses.map((w, i) => <li key={`weak-${i}-${typeof w === 'string' ? w.slice(0, 20) : i}`} className="text-xs text-[#0A0F1C]">⚠ {w}</li>)}
          </ul>
        </div>
      )}

      {r.in_your_voice_rewrites?.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-3" data-testid="creator-rewrites">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">In your voice - rewrites</div>
          {r.in_your_voice_rewrites.map((rw, i) => (
            <div key={rw.title || `rewrite-${i}`} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3" data-testid={`rewrite-${i}`}>
              <div className="flex items-center justify-between mb-1">
                <strong className="text-sm text-[#0A0F1C]">{rw.title}</strong>
                <button type="button" onClick={() => onUseRewrite(rw)} className="text-[10px] text-[#1D4ED8] font-semibold underline">Use in Generator →</button>
              </div>
              <p className="text-xs text-[#475569] italic">"{rw.hook}"</p>
              {rw.problem && <p className="text-xs text-[#64748B] mt-1"><strong>Problem:</strong> {rw.problem}</p>}
              {rw.solution && <p className="text-xs text-[#64748B] mt-1"><strong>Solution:</strong> {rw.solution}</p>}
              {rw.cta && <p className="text-xs text-[#64748B] mt-1"><strong>CTA:</strong> {rw.cta}</p>}
              {rw.why_this_works_for_you && <p className="text-[10px] text-emerald-700 mt-1">Why this works for you: {rw.why_this_works_for_you}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PerformanceTimeline({ metrics, totals, onDelete }) {
  if (!metrics?.length) {
    return (
      <div className="bg-white border border-dashed border-[#CBD5E1] rounded-2xl p-8 text-center" data-testid="perf-empty">
        <Gauge size={28} className="mx-auto text-[#94A3B8]" aria-hidden="true" />
        <p className="text-sm text-[#475569] mt-2">No metrics logged for this script yet.</p>
      </div>
    );
  }
  const maxViews = Math.max(...metrics.map((m) => m.views || 0), 1);
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-3" data-testid="perf-timeline">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total views" value={totals.views.toLocaleString()} />
        <Stat label="Engagement" value={totals.engagement.toLocaleString()} />
        <Stat label="Engagement rate" value={`${totals.avg_engagement_rate}%`} />
      </div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Timeline</div>
      <ul className="space-y-2">
        {metrics.map((m) => (
          <li key={m.metric_id} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3" data-testid={`perf-row-${m.metric_id}`}>
            <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
              <span className="text-xs font-semibold text-[#0A0F1C]">{(m.posted_at || '').slice(0, 10)}</span>
              <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{m.platform}</span>
              <button type="button" onClick={() => onDelete(m.metric_id)} aria-label="Delete metric" className="text-[#94A3B8] hover:text-red-600">
                <Trash2 size={11} aria-hidden="true" />
              </button>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-bold text-[#0A0F1C] w-20">{(m.views || 0).toLocaleString()} views</span>
              <div className="flex-1 h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                <div className="h-full bg-[#1D4ED8] rounded-full" style={{ width: `${((m.views || 0) / maxViews) * 100}%` }} />
              </div>
              <span className="text-[10px] text-[#94A3B8] whitespace-nowrap">ER {m.engagement_rate}%</span>
            </div>
            <div className="text-[10px] text-[#475569] mt-1">
              Likes {m.likes} · Comments {m.comments} · Shares {m.shares} · Saves {m.saves}
              {m.follows_gained ? ` · +${m.follows_gained} follows` : ''}
              {m.link_clicks ? ` · ${m.link_clicks} clicks` : ''}
            </div>
            {m.notes && <div className="text-[10px] text-[#94A3B8] mt-1">{m.notes}</div>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2.5 text-center">
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{label}</div>
      <div className="text-lg font-black text-[#0A0F1C]">{value}</div>
    </div>
  );
}

function InsightsView({ insight }) {
  const r = insight?.report || {};
  return (
    <div className="space-y-3" data-testid="insights-view">
      {r.summary && (
        <p className="text-sm text-[#0A0F1C] leading-relaxed bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">{r.summary}</p>
      )}
      {r.what_is_working?.length > 0 && (
        <div data-testid="insights-working">
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-1">What's working</div>
          <ul className="space-y-1.5">
            {r.what_is_working.map((w, i) => (
              <li key={w.insight || `working-${i}`} className="text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                <strong className="text-[#0A0F1C]">{w.insight}</strong>
                {w.evidence && <div className="text-[#475569] mt-0.5">Evidence: {w.evidence}</div>}
                {w.action && <div className="text-emerald-700 mt-0.5">→ {w.action}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {r.what_to_stop?.length > 0 && (
        <div data-testid="insights-stop">
          <div className="text-[10px] font-bold uppercase tracking-wider text-red-700 mb-1">What to stop / fix</div>
          <ul className="space-y-1.5">
            {r.what_to_stop.map((w, i) => (
              <li key={w.insight || `stop-${i}`} className="text-xs bg-red-50 border border-red-200 rounded-lg p-2">
                <strong className="text-[#0A0F1C]">{w.insight}</strong>
                {w.evidence && <div className="text-[#475569] mt-0.5">Evidence: {w.evidence}</div>}
                {w.action && <div className="text-red-700 mt-0.5">→ {w.action}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {r.experiments?.length > 0 && (
        <div data-testid="insights-experiments">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#1D4ED8] mb-1">Experiments to try</div>
          <ul className="space-y-1.5">
            {r.experiments.map((e, i) => (
              <li key={e.name || `exp-${i}`} className="text-xs bg-[#EFF6FF] border border-[#DBEAFE] rounded-lg p-2">
                <strong className="text-[#0A0F1C]">{e.name}</strong>
                {e.hypothesis && <div className="text-[#475569] mt-0.5">Hypothesis: {e.hypothesis}</div>}
                {e.how_to_test && <div className="text-[#1D4ED8] mt-0.5">→ {e.how_to_test}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}



export { TrendReportView, CreatorAnalysisView, PerformanceTimeline, Stat, InsightsView };
